import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DataGrid, DatePicker, minutesToHm, todayIso } from '@eo/shared';

import { flattenTimeEntries, memberDayHref } from './helpers';
import type { TimeEntryRow, TimeTrackingTabProps } from './types';
import { makeTimeGridConfig } from './config/gridConfig';

export function TimeTrackingTab({
  date,
  timeEntries,
  staff,
  options,
  onQueryOptions,
}: TimeTrackingTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const rows = useMemo(() => flattenTimeEntries(timeEntries), [timeEntries]);
  const selectedCfo = searchParams.get('cfo');
  // The pick is an email in the URL; its name comes from wherever it is known.
  const selectedName = selectedCfo
    ? (options.users.items.find((u) => u.email === selectedCfo)?.name ??
      timeEntries.find((c) => c.email === selectedCfo)?.name ??
      staff.find((s) => s.email === selectedCfo)?.name ??
      selectedCfo)
    : null;
  const members = options.users;
  const config = useMemo(
    () =>
      makeTimeGridConfig({
        members,
        selectedLabel: selectedName ?? undefined,
        onQuery: (q) => onQueryOptions({ source: 'users', ...q }),
      }),
    [members, selectedName, onQueryOptions],
  );

  const setParam = (key: string, value: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === null) next.delete(key);
      else next.set(key, value);
      return next;
    });
  };

  const onDateChange = (value: string) => {
    if (!value) return;
    setParam('date', value);
  };

  return (
    <section className="flex flex-col gap-4">
      <p className="text-sm text-text-tertiary">
        {selectedCfo === null
          ? "Every active member's logged time for the selected date."
          : 'Logged time for the selected member and date.'}
      </p>

      {/* Per-member totals and setup gaps stay visible: they are about the
          person, not the entries, so they would vanish in a flat grid. */}
      <div className="flex flex-wrap gap-3">
        {timeEntries.map((cfo) => (
          <div
            key={cfo.userId}
            className="flex min-w-52 flex-col gap-1 rounded-md border border-border-faint p-3"
          >
            <h2 className="text-sm font-semibold text-text-primary">{cfo.name}</h2>
            {cfo.error ? (
              <span className="text-xs text-text-tertiary">{cfo.error}</span>
            ) : (
              <span className="text-sm text-text-tertiary">
                Total: {minutesToHm(cfo.totalMinutes)}
              </span>
            )}
          </div>
        ))}
      </div>

      <DataGrid<TimeEntryRow>
        config={config}
        rows={rows}
        headerActions={
          <div className="flex items-center gap-3">
            <DatePicker
              value={date}
              onChange={onDateChange}
              ariaLabel="Time tracking date"
              maxDate={todayIso()}
              className="w-40"
            />
            {selectedCfo && (
              <Link
                to={memberDayHref(selectedCfo, date)}
                className="text-sm font-medium text-primary hover:underline"
              >
                Open {selectedName}'s day
              </Link>
            )}
          </div>
        }
      />
    </section>
  );
}
